import { useRef, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import type { PermissionInfo } from '../types';

interface PermissionTreeProps {
  orderedCategories: string[];
  categoriesMap: Record<string, PermissionInfo[]>;
  selected: Set<string>;
  editable: boolean;
  onToggle: (key: string) => void;
  onToggleCategory: (category: string, checked: boolean) => void;
  expandedCategories: Set<string>;
  onToggleExpand: (category: string) => void;
  searchQuery?: string;
}

function CategoryCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-60 flex-shrink-0"
    />
  );
}

export default function PermissionTree({
  orderedCategories,
  categoriesMap,
  selected,
  editable,
  onToggle,
  onToggleCategory,
  expandedCategories,
  onToggleExpand,
  searchQuery = '',
}: PermissionTreeProps) {
  const query = searchQuery.trim().toLowerCase();

  const matchesQuery = (perm: PermissionInfo) =>
    !query ||
    perm.name.toLowerCase().includes(query) ||
    perm.key.toLowerCase().includes(query) ||
    (perm.description || '').toLowerCase().includes(query);

  const visibleCategories = orderedCategories
    .map((category) => ({
      category,
      perms: (categoriesMap[category] || []).filter(matchesQuery),
    }))
    .filter(({ perms }) => perms.length > 0);

  if (visibleCategories.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic text-center py-8">
        Nessun permesso trovato
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {visibleCategories.map(({ category, perms }) => {
        const total = perms.length;
        const selectedCount = perms.filter((p) => selected.has(p.key)).length;
        const isExpanded = expandedCategories.has(category) || query.length > 0;

        return (
          <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
            <div
              role="button"
              tabIndex={0}
              onClick={() => onToggleExpand(category)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggleExpand(category);
                }
              }}
              className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 cursor-pointer select-none"
            >
              <ChevronRight
                className={`h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
              />
              <CategoryCheckbox
                checked={total > 0 && selectedCount === total}
                indeterminate={selectedCount > 0 && selectedCount < total}
                disabled={!editable}
                onChange={(checked) => onToggleCategory(category, checked)}
              />
              <span className="text-sm font-semibold text-gray-800 flex-1 truncate">{category}</span>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                  selectedCount > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                }`}
              >
                {selectedCount}/{total}
              </span>
            </div>

            {isExpanded && (
              <div className="py-1.5 px-3 space-y-0.5 bg-white border-t border-gray-100">
                {perms.map((perm) => (
                  <label
                    key={perm.key}
                    className={`flex items-start gap-2.5 py-1.5 pl-6 pr-2 ml-2 border-l-2 border-gray-100 rounded hover:bg-gray-50 ${
                      editable ? 'cursor-pointer' : 'cursor-default'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(perm.key)}
                      disabled={!editable}
                      onChange={() => onToggle(perm.key)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-60 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{perm.name}</p>
                      {perm.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{perm.description}</p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
